import React from 'react';
import { Box, Typography, List, ListItem, Chip, styled } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { OperatingHours, DayHours, TimeBreak } from '@lov/shared';

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const DAY_LABELS: Record<(typeof DAYS)[number], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

interface OperatingHoursDisplayProps {
  operatingHours: OperatingHours | null | undefined;
  variant?: 'compact' | 'full';
  highlightToday?: boolean;
}

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{ isToday?: boolean }>(({ theme, isToday }) => ({
  padding: '12px 0',
  borderRadius: 8,
  backgroundColor: isToday ? theme.palette.action.hover : 'transparent',
  paddingLeft: isToday ? 12 : 0,
  paddingRight: isToday ? 12 : 0,
  marginBottom: 4,
}));

const DayLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{ isToday?: boolean }>(({ theme, isToday }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  fontWeight: isToday ? 600 : 500,
  color: isToday ? theme.palette.primary.main : theme.palette.text.primary,
  minWidth: 100,
}));

const HoursText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const CompactContainer = styled(Box)({
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
});

/**
 * Formats time from 24-hour format to 12-hour format with AM/PM
 * @param time - Time in HH:mm format (e.g., "09:00", "14:30")
 * @returns Formatted time (e.g., "9:00 AM", "2:30 PM")
 */
function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Gets the current day of the week in lowercase
 * @returns Day name (e.g., "monday", "tuesday")
 */
function getTodayKey(): (typeof DAYS)[number] {
  const dayIndex = new Date().getDay();
  // Convert Sunday (0) to 6, and shift others down by 1
  const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
  return DAYS[adjustedIndex];
}

/**
 * Formats an array of breaks into a display string
 */
function formatBreaks(breaks: TimeBreak[]): string {
  const formatted = breaks.map((b) => `${formatTime(b.start)} - ${formatTime(b.end)}`).join(', ');
  if (breaks.length === 1) {
    return `(Break: ${formatted})`;
  }
  return `(Breaks: ${formatted})`;
}

/**
 * Renders the hours for a single day
 */
function renderDayHours(dayHours: DayHours | undefined): React.ReactNode {
  if (!dayHours || dayHours.closed) {
    return <Chip label="Closed" size="small" color="default" />;
  }

  if (dayHours.is24Hours) {
    return <Chip label="Open 24 Hours" size="small" color="primary" />;
  }

  const hasBreaks = dayHours.breaks && dayHours.breaks.length > 0;

  return (
    <HoursText>
      {dayHours.open ? formatTime(dayHours.open) : '--:--'} -{' '}
      {dayHours.close ? formatTime(dayHours.close) : '--:--'}
      {hasBreaks && ` ${formatBreaks(dayHours.breaks!)}`}
    </HoursText>
  );
}

export const OperatingHoursDisplay: React.FC<OperatingHoursDisplayProps> = ({
  operatingHours,
  variant = 'full',
  highlightToday = false,
}) => {
  // Handle empty or null operating hours
  if (!operatingHours || Object.keys(operatingHours).length === 0) {
    return (
      <Typography
        sx={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontSize: '0.875rem',
          color: 'text.secondary',
          fontStyle: 'italic',
        }}
      >
        Operating hours not configured
      </Typography>
    );
  }

  const todayKey = getTodayKey();

  // Compact variant - horizontal chips
  if (variant === 'compact') {
    const todayHours = operatingHours[todayKey];
    return (
      <CompactContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Today:
          </Typography>
          {renderDayHours(todayHours)}
        </Box>
      </CompactContainer>
    );
  }

  // Full variant - list of all days
  return (
    <Box>
      <List sx={{ padding: 0 }}>
        {DAYS.map((day) => {
          const isToday = highlightToday && day === todayKey;
          const dayHours = operatingHours[day];

          return (
            <StyledListItem key={day} isToday={isToday}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <DayLabel isToday={isToday}>
                  {DAY_LABELS[day]}
                  {isToday && (
                    <Chip
                      label="Today"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                    />
                  )}
                </DayLabel>
                <Box>{renderDayHours(dayHours)}</Box>
              </Box>
            </StyledListItem>
          );
        })}
      </List>
    </Box>
  );
};
